import React, { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { FinanceType, WebStep } from '@upstart/harmony/lib/legos/constants';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import { OfferCategory } from '@upstart/harmony/lib/legos/paymentTypes';
import { formatPhoneNumber } from '@upstart/harmony/lib/phone-number-utils';
import {
  ShareDealDialog,
  ShareDealDialogDealConfig,
  ShareDealDialogFormProps,
  ShareDealDialogSendConfig,
  ShareDealDialogStep,
} from '@upstart/desking-components';
import { sendData, getCarInfo } from 'util/amplitude';
import { amplitudeEventsShareDeal } from 'util/amplitudeEvents';

import './StickerModal.scss';
import {
  StepSource,
  buildPencilForRequest,
  useCallbackAfterStickerSent,
  useStickerConfig,
  useStickerUrl,
  useWebSteps,
} from './utils';

export interface SendStickerOptions {
  email?: string;
  message: string;
  sendToPhone: boolean;
  defaultWebStep: WebStep;
  disabledSteps: Record<WebStep, boolean>;
  includeSticker: boolean;
}

interface StickerModalProps {
  deal: Deal;
  car: Car;
  dealer: Dealer;
  userForMessage?: User | null;
  financeType: FinanceType;
  isOpen: boolean;
  onClose: () => void;
  onPrint: (pencilWithStickerData: Pencil) => void;
  onSend: (
    stickerData: Pencil['stickerData'],
    sendOptions: SendStickerOptions,
    stickerPencil: Pencil,
  ) => void;
  pencil: Pencil;
  offerCategory?: OfferCategory | null;
  deskingId: string;
}

const sendAmplitudeEvent = (event: string, car: Car) => {
  sendData(event, getCarInfo(car));
};

const StickerModal: React.FC<StickerModalProps> = ({
  deal,
  car,
  dealer,
  userForMessage,
  financeType,
  isOpen,
  onClose,
  onPrint,
  onSend,
  pencil,
  offerCategory,
  deskingId,
}) => {
  const [dialogStep, setDialogStep] = useState<ShareDealDialogStep>('form');
  useEffect(() => {
    if (!isOpen) {
      setDialogStep('form');
    }
  }, [isOpen]);
  useCallbackAfterStickerSent(() => setDialogStep('sent-success'), isOpen);

  useEffect(() => {
    if (isOpen && dialogStep === 'form') {
      sendAmplitudeEvent(amplitudeEventsShareDeal.SHARE_DEAL_START, car);
    }
  }, [isOpen]);

  const applicant = deal.applicant || {};
  const isPhoneKnown = !!applicant.phone;
  const [customerMessage, setCustomerMessage] = useState(
    () =>
      `Hi ${deal.applicant?.firstName ||
        'shopper'}, this is ${userForMessage?.firstName ||
        'your sales representative'}. Thank you for choosing ${
        dealer.name
      }. Here is a link to your deal.`,
  );

  const [stickerConfig, setStickerConfig] = useStickerConfig(
    deal,
    car,
    pencil,
    dealer,
  );

  const [sendConfig, setSendConfig] = useState<ShareDealDialogSendConfig>({
    shouldSendEmail: true,
    email: applicant.email || '',
    shouldSendSms: isPhoneKnown,
    phone: formatPhoneNumber(applicant.phone),
    disablePhone: !isPhoneKnown,
    disablePhoneInput: true,
  });

  const webStepsSource = useWebSteps(dealer);
  const buildDealConfig = (
    stepSources: StepSource[],
  ): ShareDealDialogDealConfig<WebStep> => ({
    loadingSteps: stepSources.length === 0,
    stepsSource: stepSources,
    includedSteps: stepSources.map(s => s.value),
    startStep: stepSources[0]?.value || WebStep.payments,
  });
  const [dealConfig, setDealConfig] = useState<
    ShareDealDialogDealConfig<WebStep>
  >(() => buildDealConfig(webStepsSource));
  useEffect(() => {
    setDealConfig(buildDealConfig(webStepsSource));
  }, [webStepsSource, setDealConfig]);

  const pencilDataForRequest = useMemo(
    () => buildPencilForRequest(pencil, stickerConfig.selectedOffers),
    [pencil, stickerConfig.selectedOffers],
  );

  const pencilStickerData = useMemo(
    (): Pencil['stickerData'] => ({
      financeType,
      disclaimer: stickerConfig.disclaimer,
      expiryDate: moment(stickerConfig.expirationDate).format('MM/DD/YYYY'),
      offerCategory,
    }),
    [
      financeType,
      stickerConfig.disclaimer,
      stickerConfig.expirationDate,
      offerCategory,
    ],
  );

  const stickerUrl = useStickerUrl(
    dialogStep,
    pencilDataForRequest,
    pencilStickerData,
    deskingId,
  );

  const formProps: ShareDealDialogFormProps<WebStep, string, Moment> = {
    customerMessage,
    onCustomerMessageChange: e => setCustomerMessage(e.target.value),
    sendConfig,
    onSendConfigChange: setSendConfig,
    dealConfig,
    onDealConfigChange: setDealConfig,
    stickerConfig,
    onStickerConfigChange: setStickerConfig,
  };

  const previewProps = {
    stickerUrl,
    onPrintClicked: () =>
      onPrint({ ...pencilDataForRequest, stickerData: pencilStickerData }),
  };

  const getSendStickerOptions = (): SendStickerOptions => {
    const disabledSteps = Object.fromEntries(
      webStepsSource.map((step): [WebStep, boolean] => [
        step.value,
        !dealConfig.includedSteps.includes(step.value),
      ]),
    ) as Record<WebStep, boolean>;

    return {
      email: sendConfig.shouldSendEmail ? sendConfig.email : undefined,
      message: customerMessage,
      sendToPhone: sendConfig.shouldSendSms,
      defaultWebStep: dealConfig.startStep,
      disabledSteps,
      includeSticker: stickerConfig.includeWhenSending,
    };
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ShareDealDialog
        formProps={formProps}
        previewProps={previewProps}
        open={isOpen}
        onClose={() => {
          onClose();
          sendAmplitudeEvent(amplitudeEventsShareDeal.SHARE_DEAL_CANCEL, car);
        }}
        step={dialogStep}
        onStepChange={newStep => {
          if (dialogStep === 'preview' && newStep === 'form') {
            sendAmplitudeEvent(
              amplitudeEventsShareDeal.SHARE_DEAL_PREVIEW_STICKER_EDIT_TERMS_CLICKED,
              car,
            );
          }
          if (newStep === 'preview') {
            sendAmplitudeEvent(
              amplitudeEventsShareDeal.SHARE_DEAL_PREVIEW_STICKER_VIEWED,
              car,
            );
          }
          setDialogStep(newStep);
        }}
        onSubmit={() => {
          onSend(
            pencilStickerData,
            getSendStickerOptions(),
            pencilDataForRequest,
          );
        }}
      />
    </LocalizationProvider>
  );
};

export default StickerModal;
